import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { supabase } from '../../lib/supabaseClient';
import { ChevronLeft, ChevronRight, Users, Link as LinkIcon, Calendar } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from 'date-fns';

interface ReferralStats {
  code: string;
  visits: number;
  signups: number;
  lastVisit: string;
}

type TimePeriod = 'lifetime' | '7days' | '30days' | 'today';

const ITEMS_PER_PAGE = 10;

export const ReferralsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('lifetime');

  const { data: referralStats, isLoading } = useQuery<ReferralStats[]>(
    ['referralStats', timePeriod],
    async () => {
      // Get visit counts
      const { data: visits, error: visitsError } = await supabase
        .from('referrals')
        .select('code, created_at')
        .order('created_at', { ascending: false });

      if (visitsError) throw visitsError;

      // Get signup counts
      const { data: signups, error: signupsError } = await supabase
        .from('user_referrals')
        .select('referral_code, created_at')
        .order('created_at', { ascending: false });

      if (signupsError) throw signupsError;

      // Get date range for filtering
      const now = new Date();
      const dateRange = {
        start: timePeriod === 'lifetime' ? new Date(0) :
               timePeriod === '30days' ? subDays(now, 30) :
               timePeriod === '7days' ? subDays(now, 7) :
               startOfDay(now),
        end: endOfDay(now)
      };

      // Combine and aggregate data
      const statsMap = new Map<string, ReferralStats>();

      // Process visits within date range
      visits?.forEach(visit => {
        const visitDate = new Date(visit.created_at);
        if (isWithinInterval(visitDate, dateRange)) {
          const existing = statsMap.get(visit.code) || {
            code: visit.code,
            visits: 0,
            signups: 0,
            lastVisit: visit.created_at
          };
          existing.visits++;
          statsMap.set(visit.code, existing);
        }
      });

      // Process signups within date range
      signups?.forEach(signup => {
        const signupDate = new Date(signup.created_at);
        if (isWithinInterval(signupDate, dateRange)) {
          const existing = statsMap.get(signup.referral_code) || {
            code: signup.referral_code,
            visits: 0,
            signups: 0,
            lastVisit: ''
          };
          existing.signups++;
          statsMap.set(signup.referral_code, existing);
        }
      });

      return Array.from(statsMap.values())
        .sort((a, b) => b.visits - a.visits);
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      staleTime: 15000
    }
  );

  const totalPages = Math.ceil((referralStats?.length || 0) / ITEMS_PER_PAGE);
  const currentStats = referralStats?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTimePeriodChange = (period: TimePeriod) => {
    setTimePeriod(period);
    setCurrentPage(1); // Reset to first page when changing time period
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LinkIcon className="w-6 h-6 text-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Referral Analytics
            </h1>
          </div>
          {referralStats && referralStats.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {referralStats.length} referral codes tracked
            </span>
          )}
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Track referral visits and successful signups
        </p>

        {/* Time Period Filter */}
        <div className="mt-4 flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div className="flex gap-2">
            {[
              { value: 'lifetime', label: 'All Time' },
              { value: '30days', label: 'Last 30 Days' },
              { value: '7days', label: 'Last 7 Days' },
              { value: 'today', label: 'Today' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => handleTimePeriodChange(value as TimePeriod)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  timePeriod === value
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : !referralStats || referralStats.length === 0 ? (
          <div className="text-center py-12">
            <LinkIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No referrals tracked yet
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Start sharing your referral links to track visits and signups
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Referral Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total Visits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Signups
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Conversion Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Visit
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentStats?.map((stat) => {
                    const conversionRate = stat.visits > 0 
                      ? ((stat.signups / stat.visits) * 100).toFixed(1)
                      : '0.0';

                    return (
                      <tr key={stat.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {stat.code}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {stat.visits.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 text-green-500 mr-1.5" />
                            <span className="text-sm text-gray-900 dark:text-white">
                              {stat.signups.toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            parseFloat(conversionRate) > 5
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : parseFloat(conversionRate) > 2
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {conversionRate}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {stat.lastVisit ? format(new Date(stat.lastVisit), 'PPp') : 'Never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;
                    const isNearCurrent = Math.abs(pageNumber - currentPage) <= 1;
                    const isEndPage = pageNumber === 1 || pageNumber === totalPages;

                    if (!isNearCurrent && !isEndPage) {
                      if (pageNumber === 2 || pageNumber === totalPages - 1) {
                        return <span key={pageNumber} className="px-2 py-1 text-gray-400 dark:text-gray-500">...</span>;
                      }
                      return null;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          isCurrentPage
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};